import React, { useState, useCallback, useEffect, useContext } from 'react'
import {
  GoogleMap,
  StreetViewPanorama,
  useJsApiLoader,
} from '@react-google-maps/api'
import {
  streetViewClosed,
  streetViewOpened,
  updatePrincipalBounds,
  updateZoom,
} from 'redux/features/map/slice'
import {
  makeSelectPrincipalBounds,
  makeSelectIsStreetViewOpen,
} from 'redux/features/map/selectors'
import { useAppSelector, useAppDispatch } from 'redux/hooks'
import { fetchWithCheckAuthorization } from 'utils/fetchWithAuthorization'
import { MapContext } from 'contexts/MapContextProvider'
import { Button, Spinner } from 'react-bootstrap'
import { FaChevronLeft } from 'react-icons/fa'
import './Map.scss'

const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
const containerStyle = {
  width: '100%',
  height: '100%',
}
const libraries = ['geometry' as const]

interface Props {
  children?: JSX.Element
}

const defaultProps = {
  children: undefined,
}

const Map = ({ children }: Props): JSX.Element => {
  const principalBounds = useAppSelector(makeSelectPrincipalBounds)
  const isStreetViewOpen = useAppSelector(makeSelectIsStreetViewOpen)
  const dispatch = useAppDispatch()
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey,
    libraries,
  })
  const { map, setMap } = useContext(MapContext)

  const [maptypeid, setMaptypeid] = useState('hybrid')

  const onLoad = useCallback(
    (googleMap) => {
      setMap(googleMap)
      googleMap.addListener('maptypeid_changed', () => {
        setMaptypeid(googleMap.getMapTypeId())
      })
    },
    [setMap]
  )

  const options = {
    zoomControlOptions: {
      position: window.google?.maps.ControlPosition.RIGHT_BOTTOM,
    },
    mapTypeControlOptions: {
      style: window.google?.maps.MapTypeControlStyle.DEFAULT,
      position: window.google?.maps.ControlPosition.TOP_LEFT,
    },
    mapTypeControl: true,
    fullscreenControl: false,
    mapTypeId: maptypeid,
    keyboardShortcuts: false,
  }

  // Set bounds from user authorized bounds onLoad
  useEffect(() => {
    if (map && principalBounds) {
      map.fitBounds(principalBounds)
    }
  }, [principalBounds, map])

  useEffect(() => {
    ;(async () => {
      const response = await fetchWithCheckAuthorization(
        `/api/agent/codes-postaux/principal-bounds`
      )
      // Défaut: centre géographique de la France métropolitaine
      let userBounds = {
        east: 9.3336,
        north: 51.0418,
        west: 5.0904,
        south: 41.19,
      }
      if (response.ok) {
        userBounds = await response.json()
      }
      dispatch(updatePrincipalBounds(userBounds))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- It triggers an error
  }, [])

  const removeStreetView = () => {
    map?.getStreetView().setVisible(false)
  }

  const handleStreetViewVisibilityChanged = () => {
    if (map?.getStreetView().getVisible() === true) {
      dispatch(streetViewOpened())
    } else {
      dispatch(streetViewClosed())
    }
  }

  return (
    <div id="geociblage-map" className="map">
      {isStreetViewOpen && (
        <Button className="exitStreetView" onClick={removeStreetView}>
          <FaChevronLeft /> Sortir du Street View
        </Button>
      )}
      {isLoaded && principalBounds ? (
        <GoogleMap
          mapContainerStyle={containerStyle}
          onLoad={onLoad}
          options={options}
          onZoomChanged={() => dispatch(updateZoom(map?.getZoom()))}
        >
          <StreetViewPanorama
            onVisibleChanged={handleStreetViewVisibilityChanged}
          />
          {children}
        </GoogleMap>
      ) : (
        <Spinner animation="border" role="status" />
      )}
    </div>
  )
}
Map.defaultProps = defaultProps
export default React.memo(Map)
